import {Injectable} from '@angular/core';
import {ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot} from '@angular/router';

import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import * as moment from 'moment';

import {AuthApiService, HttpStatusCodesEnum, ServerResponseData} from '@spa/api';

import {Helper} from 'appShared/classes';
import {PanelPaths} from 'appShared/paths';
import {PermissionsEnum} from 'appShared/enums';
import {CacheCollectionService} from 'appShared/services';
import {ErrorMessageIdSelector} from 'appAssets/descriptors';
import {CommonMessages} from 'appAssets/messages/common.messages';

import {PermissionRulesTrait} from '../traits';

@Injectable()
export class PermissionBasedRedirectGuardService implements CanActivate {
    constructor(
        private authenticationService: AuthApiService,
        protected router: Router,
        protected cacheCollectionService: CacheCollectionService
    ) {

    }

    private navigateToErrorPage(message: string): void {
        const timestamp = moment().unix();

        this.cacheCollectionService.addItem(timestamp.toString(), message);

        this.router.navigate(['/error'], {
            queryParams: {
                [ErrorMessageIdSelector]: timestamp
            }
        }).finally();
    }

    private getClientsPermissions(): string[] {
        return [
            PermissionsEnum.CrmClientsCreateNew,
            PermissionsEnum.CrmClientsViewDetails,
            PermissionsEnum.CrmClientsUpdateDetails
        ];
    }

    private getClientUsersPermissions(): string[] {
        return PermissionRulesTrait.getViewClientUsersPermissionRule().permissions;
    }

    private getDocumentsPermissions(): string[] {
        return [
            PermissionsEnum.CrmDocumentsCreateNew,
            PermissionsEnum.CrmDocumentsViewDetails,
            PermissionsEnum.CrmDocumentsUpdateDetails
        ];
    }

    // private getBankAccountsViewDetailsPermissions(): PermissionRuleInterface {
    //
    // }

    private getPaymentsPermissions(): string[] {
        return [
            PermissionsEnum.CrmPaymentsCreateNewTransfer,
            PermissionsEnum.CrmPaymentsCreateNewDeposit,
            PermissionsEnum.CrmPaymentsCreateNewWithdrawal,
            PermissionsEnum.CrmPaymentsViewDetails,
            PermissionsEnum.CrmPaymentsUpdateDetails
        ];
    }

    private getAccountsPermissions(): string[] {
        return [
            PermissionsEnum.CrmTradingAccountsCreateNew,
            PermissionsEnum.CrmTradingAccountsViewDetails,
            PermissionsEnum.CrmTradingAccountsUpdateDetails,
            PermissionsEnum.CrmCashAccountsCreateNew,
            PermissionsEnum.CrmCashAccountsViewDetails,
            PermissionsEnum.CrmCashAccountsUpdateDetails,
        ];
    }

    private getReportsPermissions(): string[] {
        return [PermissionsEnum.CrmReportsAll, PermissionsEnum.CrmReportsViewPredefinedReports];
    }

    private getMarketingReportsPermissions(): string[] {
        return [PermissionsEnum.CRMReportsViewMarketingReports];
    }

    private getRolesPermissions(): string[] {
        return [
            PermissionsEnum.CrmOperatorsAndRolesCreateNewRole,
            PermissionsEnum.CrmOperatorsAndRolesViewUsersRoles,
            PermissionsEnum.CrmOperatorsAndRolesUpdateRole
        ];
    }

    private getOperatorsPermissions(): string[] {
        return [
            PermissionsEnum.CrmOperatorsAndRolesCreateNewOperator,
            PermissionsEnum.CrmOperatorsAndRolesViewUser,
            PermissionsEnum.CrmOperatorsAndRolesUpdateOperator
        ];
    }

    private getGroupsPermissions(): string[] {
        return [
            PermissionsEnum.CrmClientGroupsCreateNewGroup,
            PermissionsEnum.CrmClientGroupsViewClientGroups,
            PermissionsEnum.CrmClientGroupsUpdateGroup
        ];
    }

    private getAuditPermissions(): string[] {
        return [PermissionsEnum.CrmAuditTrailAll, PermissionsEnum.CrmAuditTrailView];
    }

    private getFileImportPermissions(): string[] {
        return [PermissionsEnum.CrmFileImportAll, PermissionsEnum.CrmFileImportClientsAndAccounts];
    }

    private getTradingPlatformPermissions(): string[] {
        return [PermissionsEnum.CrmSubscriptionManagementAll, PermissionsEnum.CrmSubscriptionManagementManageTradingPlatforms];
    }

    private getEventHistoryPermissions(): string[] {
        return [PermissionsEnum.CrmSubscriptionManagementAll, PermissionsEnum.CrmSubscriptionManagementViewEventHistory];
    }

    private getMarketDataFeedPermissions(): string[] {
        return [
            PermissionsEnum.CrmSubscriptionManagementAll,
            PermissionsEnum.CrmSubscriptionManagementViewAvailableFeeds,
            PermissionsEnum.CrmSubscriptionManagementManageFeeds,
            PermissionsEnum.CrmSubscriptionManagementManageFeedBundles,
        ];
    }

    private doCheck(): any {
        return this.authenticationService.getAuthenticatedUserAllPermissionsAsync().pipe(
            map((response: ServerResponseData<boolean>) => {
                if (response.status !== HttpStatusCodesEnum.Ok) {
                    let message = CommonMessages.UnknownError.message;

                    switch (response.status) {
                        case HttpStatusCodesEnum.Unauthorized:
                            message = CommonMessages.UnauthorizedError.message;
                            break;
                        case HttpStatusCodesEnum.Forbidden:
                            message = CommonMessages.ForbiddenError.message;
                            break;
                        case HttpStatusCodesEnum.InternalServerError:
                            message = CommonMessages.InternalServerError.message;
                            break;
                    }

                    this.navigateToErrorPage(message);

                    return false;
                }

                let panelPath = '/' + PanelPaths.root + '/';

                const userPermissions = this.authenticationService.getAuthenticatedUserPermissions();

                if (userPermissions.indexOf(PermissionsEnum.CrmAllAll) >= 0) {
                    panelPath += PanelPaths.children.leads.path;

                    this.router.navigate([panelPath])
                        .finally();

                    return true;
                }

                if (Helper.getUnionStrategyPermissionsCheck(this.getClientsPermissions(), userPermissions)) {
                    panelPath += PanelPaths.children.leads.path;
                } else if (Helper.getUnionStrategyPermissionsCheck(this.getClientUsersPermissions(), userPermissions)) {
                    panelPath += PanelPaths.children.clientUsers.path;
                } else if (Helper.getUnionStrategyPermissionsCheck(this.getDocumentsPermissions(), userPermissions)) {
                    panelPath += PanelPaths.children.documents.path;
                } else if (Helper.getUnionStrategyPermissionsCheck(this.getPaymentsPermissions(), userPermissions)) {
                    panelPath += PanelPaths.children.payments.path;
                } else if (Helper.getUnionStrategyPermissionsCheck(this.getAccountsPermissions(), userPermissions)) {
                    panelPath += PanelPaths.children.accounts.path;
                } else if (Helper.getIntersectStrategyPermissionsCheck(this.getReportsPermissions(), userPermissions)) {
                    panelPath += [
                        PanelPaths.children.reports.path,
                        PanelPaths.children.reports.children.accountReports.path
                    ].join('/');
                }else if (Helper.getUnionStrategyPermissionsCheck(this.getMarketingReportsPermissions(), userPermissions)) {
                    panelPath += [
                        PanelPaths.children.reports.path,
                        PanelPaths.children.reports.children.marketing.path
                    ].join('/');
                } else if (Helper.getUnionStrategyPermissionsCheck(this.getRolesPermissions(), userPermissions)) {
                    panelPath += [
                        PanelPaths.children.settings.path,
                        PanelPaths.children.settings.children.roles.path
                    ].join('/');
                } else if (Helper.getUnionStrategyPermissionsCheck(this.getOperatorsPermissions(), userPermissions)) {
                    panelPath += [
                        PanelPaths.children.settings.path,
                        PanelPaths.children.settings.children.operators.path
                    ].join('/');
                } else if (Helper.getUnionStrategyPermissionsCheck(this.getGroupsPermissions(), userPermissions)) {
                    panelPath += [
                        PanelPaths.children.settings.path,
                        PanelPaths.children.settings.children.groups.path
                    ].join('/');
                } else if (Helper.getIntersectStrategyPermissionsCheck(this.getAuditPermissions(), userPermissions)) {
                    panelPath += [
                        PanelPaths.children.settings.path,
                        PanelPaths.children.settings.children.audit.path
                    ].join('/');
                } else if (Helper.getIntersectStrategyPermissionsCheck(this.getFileImportPermissions(), userPermissions)) {
                    panelPath += [
                        PanelPaths.children.settings.path,
                        PanelPaths.children.settings.children.fileImport.path
                    ].join('/');
                } else if (Helper.getIntersectStrategyPermissionsCheck(this.getTradingPlatformPermissions(), userPermissions)) {
                    panelPath += [
                        PanelPaths.children.platformFrontends.path,
                    ].join('/');
                } else if (Helper.getIntersectStrategyPermissionsCheck(this.getEventHistoryPermissions(), userPermissions)) {
                    panelPath += [
                        PanelPaths.children.marketData.path,
                        PanelPaths.children.marketData.children.eventHistory.path,
                    ].join('/');
                } else if (Helper.getIntersectStrategyPermissionsCheck(this.getMarketDataFeedPermissions(), userPermissions)) {
                    panelPath += [
                        PanelPaths.children.marketData.path,
                        PanelPaths.children.marketData.children.feeds.path,
                    ].join('/');
                } else {
                    this.navigateToErrorPage(CommonMessages.UnauthorizedError.message);

                    return false;
                }

                this.router.navigate([panelPath])
                    .finally();

                return true;
            })
        );
    }

    canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
        return this.doCheck();
    }
}
